import { useSearchParams } from 'react-router-dom';
import { LeadCard } from '@/components/LeadCard';
import { QuickFilters } from '@/components/QuickFilters';
import { useLeadCollection } from '@/hooks/useLeadCollection.ts';
import { useLeadFilters } from '@/hooks/useLeadFilters';
import { cn } from '@/lib/utils';
import { type ChipStatus, ChipStatusEnum } from '@/models/ui/filterChip';
import { writeLeadFiltersToSearchParams } from '@/models/ui/leadFilterSearchParams.ts';
import { LeadStatusGroups } from '@/models/ui/leadStatusGroup';
export function LeadSidebar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useLeadFilters();
  const leadCollection = useLeadCollection();
  const selectTab = (statusGroup: (typeof LeadStatusGroups.all)[number]) => {
    const nextParams = writeLeadFiltersToSearchParams(searchParams, {
      ...filters,
      statusGroup,
    });
    setSearchParams(nextParams, { replace: false });
  };
  const displayLeads = leadCollection.filter(filters);
  const activeChipStatuses = filters.statuses.filter(
    (status): status is ChipStatus => ChipStatusEnum.safeParse(status).success,
  );
  const counts = leadCollection.counts;
  const filterChipCounts = leadCollection.chipCounts(activeChipStatuses);
  const urgencyFilterCounts = leadCollection.urgencyCounts(filters.urgencies);
  const contactabilityFilterCounts = leadCollection.contactabilityCounts(
    filters.contactabilities,
  );
  return (
    <aside
      aria-label="LEADS"
      className="w-80 bg-[#e8e8e8] border-r border-gray-300 flex flex-col h-full"
    >
      <div className="bg-white p-4 pb-2 sticky top-0 z-10">
        <div className="mb-4">
          <h2 id="LeadListHeaderId" className="text-lg font-bold mb-1">
            LEADS
          </h2>
          <p className="text-xs text-gray-600">New Maintenance Leads</p>
        </div>
        <nav className="text-sm">
          <ul className="flex gap-4">
            <li>
              <button
                type="button"
                aria-labelledby="InProgressTab"
                onClick={() => selectTab(LeadStatusGroups.InProgress)}
                className={cn(
                  'flex items-center gap-2 pb-1',
                  filters.statusGroup === LeadStatusGroups.InProgress
                    ? 'border-b-2 border-black'
                    : 'text-gray-600',
                )}
                aria-current={
                  filters.statusGroup === LeadStatusGroups.InProgress
                    ? 'true'
                    : undefined
                }
              >
                <h3 id="InProgressTab" className="text-inherit font-inherit">
                  {LeadStatusGroups.InProgress.label}
                </h3>
                <span className="w-5 h-5 bg-black text-white text-xs flex items-center justify-center rounded">
                  {counts.inProgress}
                </span>
              </button>
            </li>
            <li>
              <button
                type="button"
                aria-labelledby="ArchiveTab"
                onClick={() => selectTab(LeadStatusGroups.Archive)}
                className={cn(
                  'flex items-center gap-2 pb-1',
                  filters.statusGroup === LeadStatusGroups.Archive
                    ? 'border-b-2 border-black'
                    : 'text-gray-600',
                )}
                aria-current={
                  filters.statusGroup === LeadStatusGroups.Archive
                    ? 'true'
                    : undefined
                }
              >
                <h3 id="ArchiveTab" className="text-inherit font-inherit">
                  {LeadStatusGroups.Archive.label}
                </h3>
                <span className="w-5 h-5 bg-black text-white text-xs flex items-center justify-center rounded">
                  {counts.archive}
                </span>
              </button>
            </li>
          </ul>
        </nav>
      </div>
      <div className="flex-1 overflow-auto overscroll-contain">
        {filters.statusGroup === LeadStatusGroups.InProgress && (
          <div className="sticky top-0 z-10 px-4 pt-3 pb-1 bg-[#e8e8e8]">
            <QuickFilters
              filterChipCounts={filterChipCounts}
              urgencyFilterCounts={urgencyFilterCounts}
              contactabilityFilterCounts={contactabilityFilterCounts}
            />
          </div>
        )}
        <div className="px-4 pb-3 pt-1">
          <ul aria-labelledby="LeadListHeaderId" className="space-y-3 pb-4">
            {displayLeads.map((lead) => (
              <LeadCard key={lead.id} lead={lead} searchTerm={filters.search} />
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}
